import { createTheme, createTokenContract } from "dx-styles"

/**
 * The token contract — the *names* the design system styles against. Every
 * leaf resolves to a `var(--dx-…)` reference, so components never hard-code a
 * value and a theme can be swapped at any DOM boundary.
 *
 * Ported from `theme/tokens/*` and `theme/utilities.ts`, flattened one level:
 * Panda's `{ DEFAULT, onDark, muted }` nesting becomes `text`, `textOnDark`,
 * `textDescription`, because dx-styles has no `DEFAULT` convention.
 */
export const tokens = createTokenContract(
  {
    color: {
      surface: null,
      outline: null,
      text: null,
      textOnDark: null,
      textDescription: null,
      hover: null,
      primary: null,
      secondary: null,
      accent: null,
      accentDark: null,
      accentSubtle: null,
      warning: null,
      error: null,
      success: null,
    },
    // `theme/utilities.ts` → spacingTokens
    space: {
      none: null,
      xs: null,
      sm: null,
      md: null,
      lg: null,
    },
    // `theme/utilities.ts` → sizesTokens, the control-height scale
    size: {
      xs: null,
      sm: null,
      md: null,
      lg: null,
    },
    // `theme/utilities.ts` → radii. Panda's `default` is `sm` here.
    radius: {
      none: null,
      sm: null,
      md: null,
      full: null,
    },
    // `theme/utilities.ts` → borderTokens, full `border` shorthands
    border: {
      default: null,
      thick: null,
    },
    font: {
      body: null,
      title: null,
    },
    // Named after the elements they style, so the ramp and the document
    // outline stay in step. `pageTitle`/`sectionTitle`/`heading` in the Panda
    // source map to h1/h2/h3.
    fontSize: {
      body: null,
      h3: null,
      h2: null,
      h1: null,
    },
    lineHeight: {
      body: null,
      tight: null,
      normal: null,
    },
    fontWeight: {
      regular: null,
      semibold: null,
    },
    duration: {
      fast: null,
      normal: null,
      slow: null,
    },
    opacity: {
      muted: null,
    },
    // `theme/utilities.ts` creates `shadow` with no token scale, so it is a
    // boolean utility over a single value — `<Box shadow>` → `shadows.base`.
    // One token, not a scale.
    shadow: {
      base: null,
    },
    gradient: {
      weak: null,
    },
  },
  { prefix: "dx" },
)

const fontFallback =
  "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif"

/**
 * The default theme. Apply the returned class at a stable boundary — the app
 * shell or `<html>` — and every token below it resolves.
 */
export const dxTheme = createTheme(tokens, {
  color: {
    surface: "#ffffff",
    outline: "#8f93a3",
    text: "#0c1436",
    textOnDark: "#fcfcfc",
    textDescription: "#5c6278",
    hover: "#f4f4f6",
    primary: "#7e4797",
    secondary: "#e7e8eb",
    accent: "#623875",
    accentDark: "#462853",
    accentSubtle: "#f8ebff",
    warning: "#ff9903",
    error: "#ff375b",
    success: "#33c4b3",
  },
  space: {
    none: "0",
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "2rem",
  },
  size: {
    xs: "24px",
    sm: "30px",
    md: "40px",
    lg: "50px",
  },
  radius: {
    none: "0px",
    sm: "4px",
    md: "8px",
    full: "9999px",
  },
  border: {
    default: "1px solid #e7e8eb",
    thick: "2px solid #e7e8eb",
  },
  font: {
    body: `"Poppins", ${fontFallback}`,
    title: `"Noto Serif Display", Georgia, serif`,
  },
  fontSize: {
    body: "12px",
    h3: "14px",
    h2: "16px",
    h1: "24px",
  },
  lineHeight: {
    body: "1.2rem",
    tight: "1.2",
    normal: "1.4",
  },
  // `theme/tokens/fontWeights.ts` names 600 `bold`, but every text style asks
  // for `semibold`. Kept as `semibold` so the text styles resolve.
  fontWeight: {
    regular: "400",
    semibold: "600",
  },
  duration: {
    fast: "100ms",
    normal: "200ms",
    slow: "300ms",
  },
  opacity: {
    muted: "0.67",
  },
  // No value in the Panda source to port — `shadows.base` is referenced by
  // `utilities.ts` but never defined in `theme/tokens/`. This is a placeholder.
  shadow: {
    base: "0 2px 8px rgba(12, 20, 54, 0.12)",
  },
  gradient: {
    weak: "linear-gradient(260deg, rgba(255, 153, 3, 0.00) 23.47%, rgba(255, 55, 91, 0.06) 47.05%, rgba(51, 196, 179, 0.00) 78.99%), linear-gradient(293deg, rgba(255, 55, 91, 0.00) 17.32%, rgba(255, 153, 3, 0.03) 48.33%, rgba(51, 196, 179, 0.06) 61.28%, rgba(51, 196, 179, 0.00) 85.76%), linear-gradient(270deg, #FFF 0%, #FAFAFA 100%)",
  },
})
